import { Helmet } from "react-helmet-async";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import ImagePreviewDialog from "@/components/ImagePreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import {
  Search,
  Star,
  TrendingUp,
  MessageSquare,
  MessageCircle,
  Shield,
  BarChart3,
  Globe,
  Users,
  FileText,
  Phone,
  Mail,
  ChevronDown,
  Target,
  Award,
  Zap,
  MapPin,
  Calendar,
  Quote,
  ThumbsUp,
  AlertTriangle,
  User,
  CheckCircle
} from "lucide-react";
import { useState, useEffect } from "react";
import { brandLogos } from "@/constants/brandLogos";
import { LANDING_COLORS } from "@/lib/landing-styles";
import { sendSiteLead, SITE_LEAD_ERROR } from "@/lib/siteLead";
import { RevealOnScroll, FloatingOrbs } from "@/components/landing";

// WhatsApp's dark teal: white text on it reads at 7:1 (the bright brand green under white text was 2.4:1).
const WHATSAPP_TEAL = '#075E54';

// Page copy as data, so every card in a row shares one markup and one style.
const GROWTH_STATS = [
  { icon: Phone, value: "20–40%", label: "More calls within 60 days" },
  { icon: TrendingUp, value: "15–30%", label: "More inquiries and bookings" },
  { icon: Star, value: "Steady growth", label: "in sales" },
];

const WHY_VISIBILITY = [
  { icon: MapPin, title: "Clients compare on Google", text: "Most people search 'specialist near me' before booking. Being visible puts you in the first choice set." },
  { icon: Star, title: "Reviews build trust", text: "Genuine reviews and professional replies influence decisions and build confidence." },
  { icon: Calendar, title: "Consistent updates win", text: "Active profiles earn more clicks, directions, and visits." },
];

// The nine service steps, each with its product screenshot.
const SERVICE_STEPS = [
  { title: "SEO audit", text: "Uncover hidden opportunities and fix critical issues blocking your Google visibility.", src: "/lovable-uploads/cf60430a-6a90-4dd7-b0f3-5f6da8cecf82.png", alt: "SEO audit dashboard showing 15 of 16 tracked keywords ranking in the top 20, an average page optimisation score of 31, a keyword rank trend chart, and a list of page title and URL problems to fix" },
  { title: "Profile optimisation", text: "Transform your profiles into customer magnets that drive more calls and visits.", src: "/lovable-uploads/ac580519-a727-4632-82e6-472909bebf92.png", alt: "Profile dashboard showing 93 percent overall completeness across 14 locations, with phone number, description, website link, categories and opening hours complete on every profile and one profile missing attributes" },
  { title: "Review responses and insights", text: "Turn every review into a growth opportunity with AI-powered responses and insights.", src: "/lovable-uploads/bf1024e6-bdde-4c8e-a36b-7b94b08e6c0b.png", alt: "Review insights screen showing a 9.7 sentiment score, a 5.0 average rating from 251 reviews, a rating trend by month, and the strengths and concerns customers mention most" },
  { title: "Product additions", text: "Easily add and manage products so customers can browse and buy directly from your profile.", src: "/lovable-uploads/9076b9e2-18cf-4c3e-b58d-857eef25109a.png", alt: "Storefront product catalogue titled See what's in store, where shoppers search the range and each phone listing carries its price, star rating and in stock label" },
  { title: "Posts and updates", text: "Share timely updates, promotions, and events to keep customers engaged and coming back.", src: "/lovable-uploads/6c2d6c61-67a8-4405-8c66-6651a6048c88.png", alt: "Posts screen with published, scheduled, draft and deleted tabs, showing clinic updates published to 11 locations at once, each with repost and stats controls" },
  { title: "Landing page or microsite", text: "Create a professional Google-linked microsite to showcase your services and attract more customers.", src: "/lovable-uploads/b6db2dda-4244-460f-bcad-0214fb1bda36.png", alt: "Landing page configuration screen with a live preview of the Kekiz The Cake Shop microsite, including its header, call button and hero banner" },
  { title: "Track profile performance", text: "See exactly how your optimisations drive more customers through your doors.", src: "/lovable-uploads/2de5924f-a971-49e7-a552-95fd1b2d4ed8.png", alt: "Performance stats across three locations for the last month: 2,874 impressions up 27 percent, 252 customer actions, a 9 percent conversion rate, and a maps versus search trend chart" },
  { title: "Competitor analysis", text: "Stay ahead by monitoring how your business stacks up against competitors in your area.", src: "/lovable-uploads/70d1b73e-f333-446f-95ff-2e6bbe8d6894.png", alt: "Competitor summary placing the business first of 45 nearby businesses overall, 20th on rating and 8th on reviews, alongside market averages and a detailed comparison table" },
  { title: "Full profile support from a Google expert", text: "Never worry about Google issues again with dedicated expert support.", src: "/lovable-uploads/5e00cf30-8fc3-401c-9674-27ab4711872e.png", alt: "A Fly support specialist wearing a headset, on a video call with the wider team" },
];

const TESTIMONIALS = [
  { name: "Riya S.", role: "Restaurant owner", quote: "\"We started seeing more calls within 3 weeks. The weekly posts and review replies make a huge difference.\"", stars: 5 },
  { name: "Aditya M.", role: "Salon owner", quote: "\"Profile optimization fixed our category mess; ranks better for 'salon near me' now.\"", stars: 5 },
  { name: "Anjali P.", role: "Retail store owner", quote: "\"They reply to every review professionally. Our rating moved from 4.1 → 4.6.\"", stars: 5 },
  { name: "Dr. Sharma", role: "Medical practice", quote: "\"Patient inquiries increased by 35% in two months. The local keyword insights were game-changing.\"", stars: 5 },
  { name: "Priya K.", role: "Fitness center owner", quote: "\"Great service and support. Weekly posts keep our profile active and engaging.\"", stars: 4 },
  { name: "Rajesh T.", role: "Service business owner", quote: "\"Professional review management helped us recover from negative feedback quickly.\"", stars: 5 },
];

const FAQS = [
  { value: "results", q: "How soon can I see results?", a: "Most specialists see measurable improvements in 45-60 days as your optimized profile gains traction and attracts more client attention." },
  { value: "ownership", q: "Do I still own my Google profile?", a: "Yes, you always own your profile. We simply optimize and manage it on your behalf while you retain complete ownership and control." },
  { value: "reviews", q: "Can you help with reviews?", a: "Yes, we help request reviews and respond professionally. Our approach helps you build a stellar online reputation that attracts more clients." },
  { value: "social-media", q: "What if I already post on social media?", a: "Fly complements your social presence by making Google work harder for you. Google Business Profile posting targets people actively searching for your services." },
  { value: "technical", q: "Do I need technical knowledge?", a: "No. Everything is managed for you; you review and approve. We handle all the technical aspects while you focus on your business." },
];

const Individuals = () => {
  const [selectedTab, setSelectedTab] = useState<"short" | "detailed">("short");
  const [api, setApi] = useState<any>(null);
  // The growth plan request reaches Anand and Manu by email (Anand, 17 Oct 26: "wire them up and let them land in
  // email to me and manu"); its fields used to go nowhere while the button opened WhatsApp.
  const [plan, setPlan] = useState({ name: "", businessName: "", phone: "", email: "" });
  const [planSend, setPlanSend] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const onPlanField = (e: React.ChangeEvent<HTMLInputElement>) => setPlan({ ...plan, [e.target.name]: e.target.value });
  const submitPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (planSend === "sending") return;
    setPlanSend("sending");
    try {
      await sendSiteLead("individuals-page", plan);
      setPlanSend("sent");
    } catch (err) {
      console.error("[Individuals] growth plan request did not send:", err);
      setPlanSend("failed");
    }
  };

  // Auto-scroll effect for carousel
  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      api.scrollNext();
    }, 2000);
    return () => clearInterval(interval);
  }, [api]);


  return (
    <div className="min-h-screen font-['Inter']">
      <Helmet>
        <title>Google Business Profile Setup for Solo Business Owners</title>
        <meta name="description" content="Turn Google searches into new clients with Google Business Profile setup, optimization, and local ranking help built for solo owners and small teams." />
        <meta name="keywords" content="Google My Business setup, local SEO services, Google My Business optimization, GMB profile management, local search optimization, Google Business Profile setup, local SEO consultation, Google My Business verification" />
        <link rel="canonical" href="https://www.fly-social.com/individuals" />

        {/* Open Graph tags */}
        <meta property="og:title" content="Google Business Profile Setup for Solo Business Owners" />
        <meta property="og:description" content="Turn Google searches into new clients with our Google My Business setup and local SEO services. Professional GMB optimization, profile management, and local ranking improvement." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fly-social.com/individuals" />
        <meta property="og:image" content="https://www.fly-social.com/lovable-uploads/26188721-2020-4a7f-bc12-f030b28db398.png" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Google Business Profile Setup for Solo Business Owners" />
        <meta name="twitter:description" content="Turn Google searches into new clients with our Google My Business setup and local SEO services. Professional GMB optimization, profile management, and local ranking improvement." />
        <meta name="twitter:image" content="https://www.fly-social.com/lovable-uploads/26188721-2020-4a7f-bc12-f030b28db398.png" />

        {/* Schema.org markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Google My Business Setup & Local SEO for Individuals",
            "description": "Professional Google My Business setup and local SEO services for individual business owners",
            "provider": {
              "@type": "Organization",
              "name": "Fly - ZoomLocal Tech",
              "url": "https://www.fly-social.com"
            },
            "serviceType": "Local SEO Services",
            "areaServed": "Worldwide",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Individual GMB Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Google My Business Setup"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Local SEO Optimization"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Google Business Profile Management"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <NavBar />
      
      {/* Hero Section */}
      <section
        className="relative pt-24 md:pt-32 pb-12 px-4 md:px-10 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0fdf4 40%, #ecfeff 100%)' }}
      >
        <FloatingOrbs variant="hero" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
            {/* Content Left */}
            <div className="lg:w-1/2 text-center lg:text-left">
              <RevealOnScroll>
                {/* Main Headline */}
                <h1
                  className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6"
                  style={{ color: LANDING_COLORS.text.primary }}
                >
                  Turn Google Searches into{" "}
                  <span style={{ color: LANDING_COLORS.primary }}>New Clients</span> & Higher Income
                </h1>

                {/* Subtext */}
                <p
                  className="text-lg md:text-xl mb-8 max-w-2xl leading-relaxed"
                  style={{ color: LANDING_COLORS.text.secondary }}
                >
                  Your expertise deserves visibility. With Fly's Google My Business (GMB) optimization, more people in your city discover, trust, and{" "}
                  <span style={{ color: LANDING_COLORS.primary }} className="font-semibold">book you</span> directly from Google Business Profile (GBP) searches.
                </p>

                {/* WhatsApp CTA */}
                <Button
                  size="lg"
                  className="text-white font-bold px-8 py-4 text-lg transition-all duration-300 hover:scale-105 rounded-2xl"
                  style={{
                    background: WHATSAPP_TEAL,
                  }}
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.open('https://wa.me/918446712833', '_blank');
                    }
                  }}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Chat on WhatsApp
                </Button>
              </RevealOnScroll>
            </div>
            
            {/* Image Right */}
            <div className="lg:w-1/2 relative">
              <RevealOnScroll delay={1} direction="right">
              <img
                src="/lovable-uploads/26188721-2020-4a7f-bc12-f030b28db398.png"
                alt="With Fly Retail, see the business growth in 60 days: GMB optimisation and review management, local ranking in the top 5 with keyword insights, and local footfall up 30 to 40 percent"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
              </RevealOnScroll>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Statistics */}
      <section className="py-16 px-4 md:px-10 bg-background">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How local businesses grow with Fly
          </h2>
          <p className="text-lg text-muted-foreground mb-12">
            More visibility means more clients, more trust, and more income.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-center">
            {GROWTH_STATS.map(({ icon: Icon, value, label }) => (
              <Card key={label} className="bg-white border border-gray-100 shadow-md rounded-xl">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-full bg-gray-100 text-gray-600">
                      <Icon className="w-8 h-8" aria-hidden />
                    </div>
                  </div>
                  <div className="text-2xl font-bold mb-3" style={{ color: LANDING_COLORS.primary }}>
                    {value}
                  </div>
                  <p className="text-lg font-semibold text-gray-900">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Authorized Google API Partner Section */}
      <section className="py-16 px-4 md:px-10 bg-violet-700">
        <div className="max-w-6xl mx-auto">
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            {[
              { icon: Shield, title: "Authorized Google API partner", sub: "Secure integration" },
              { icon: Users, title: "Used by 1000+ locations", sub: "Trusted by businesses worldwide" },
              { icon: Phone, title: "24/7 support", sub: "Always here when you need us" },
            ].map(({ icon: Icon, title, sub }) => (
              <li key={title} className="flex flex-col items-center">
                <Icon className="w-8 h-8 text-white mb-4" aria-hidden />
                <p className="text-2xl font-bold mb-2">{title}</p>
                <p className="text-white/90 text-lg">{sub}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Trusted by Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-foreground mb-10 text-center">Trusted by top consumer brands across the globe</h2>

          <div className="max-w-5xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: true,
                dragFree: true
              }}
              setApi={setApi}
              className="relative px-10"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {brandLogos.map((brand, index) => (
                  <CarouselItem key={index} className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/4 xl:basis-1/5 flex items-center justify-center">
                    <div className="h-36 sm:h-40 md:h-44 w-full flex items-center justify-center p-4 rounded-xl bg-white">
                      <img
                        src={brand.logo}
                        alt={`${brand.name} logo`}
                        className="w-full h-full object-contain"
                        style={{ maxWidth: '200px', maxHeight: '140px' }}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-0 bg-white hover:bg-white shadow-md border-0" />
              <CarouselNext className="right-0 bg-white hover:bg-white shadow-md border-0" />
            </Carousel>
          </div>
        </div>
      </section>

      {/* Why Visibility Matters */}
      <section className="py-16 px-4 md:px-10 bg-gradient-to-b from-cyan-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why <span style={{ color: LANDING_COLORS.primary }}>visibility</span> on Google matters for local business
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your expertise means nothing if clients can't find you. Here's why Google visibility directly impacts your business growth and income.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {WHY_VISIBILITY.map(({ icon: Icon, title, text }) => (
              <Card key={title} className="bg-white border border-gray-100 shadow-md rounded-xl">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-full bg-yellow-100 text-gray-700">
                      <Icon className="w-8 h-8" aria-hidden />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How Fly Transforms Your Google Presence */}
      <section className="py-16 px-4 md:px-10 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Fly Retail transforms your Google My Business presence
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nine proven steps that turn your Google Business Profile (GBP) and GMB listings into powerful customer acquisition engines.
            </p>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICE_STEPS.map((step, i) => (
              <li key={step.title}>
                <Card className="h-full bg-white border border-gray-100 shadow-md rounded-xl overflow-hidden">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold mb-4" style={{ backgroundColor: LANDING_COLORS.primary }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                    <ImagePreviewDialog src={step.src} alt={step.alt}>
                      <div className="bg-gray-50 rounded-lg h-56 mb-6 flex items-center justify-center border border-gray-200 hover:border-purple-300 transition-colors duration-300 overflow-hidden p-2 cursor-zoom-in">
                        <img src={step.src} alt={step.alt} className="w-full h-full object-contain rounded-lg" />
                      </div>
                    </ImagePreviewDialog>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.text}</p>
                  </CardContent>
                </Card>
              </li>
            ))}
          </ol>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="h-auto whitespace-normal px-8 sm:px-12 py-5 text-lg sm:text-xl text-white font-bold rounded-xl transition-colors duration-300 shadow-lg bg-violet-600 hover:bg-violet-700"
              onClick={() => {
                if (typeof document !== 'undefined') {
                  document.querySelector('[data-section="growth-plan"]')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
            >
              Get your Google profile checked today
            </Button>
          </div>
        </div>
      </section>

      {/* What Clients Say */}
      <section className="py-16 px-4 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What <span style={{ color: LANDING_COLORS.primary }}>clients</span> say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <Card key={t.name} className="bg-white border border-gray-100 shadow-md rounded-xl">
                <CardContent className="p-6">
                  <Quote className="w-6 h-6 mb-4" style={{ color: LANDING_COLORS.primary }} aria-hidden />
                  <p className="text-lg font-bold text-gray-900 mb-1">{t.name}</p>
                  <p className="text-sm text-gray-600 mb-4">{t.role}</p>
                  <blockquote className="text-gray-700 text-sm leading-relaxed mb-4">{t.quote}</blockquote>
                  <div className="flex gap-1" role="img" aria-label={`${t.stars} out of 5 stars`}>
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className={`w-4 h-4 text-yellow-400 ${i < t.stars ? 'fill-yellow-400' : ''}`} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How We Reply to Reviews */}
      <section className="py-16 px-4 md:px-10 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How we <span style={{ color: LANDING_COLORS.primary }}>reply</span> to reviews
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Professional, empathetic responses that turn feedback into opportunities and build trust with potential clients.
            </p>
          </div>
          
          {/* Toggle Switch */}
          <div className="flex justify-center mb-12">
            <div className="relative bg-gray-200 rounded-full p-1 flex">
              <div 
                className="absolute top-1 bottom-1 bg-white rounded-full shadow-md transition-all duration-300 ease-in-out"
                style={{
                  left: selectedTab === "short" ? "4px" : "50%",
                  right: selectedTab === "short" ? "50%" : "4px"
                }}
              />
              <button
                type="button"
                aria-pressed={selectedTab === "short"}
                onClick={() => setSelectedTab("short")}
                className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                  selectedTab === "short" 
                    ? "text-gray-900" 
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Short
              </button>
              <button
                type="button"
                aria-pressed={selectedTab === "detailed"}
                onClick={() => setSelectedTab("detailed")}
                className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-300 ${
                  selectedTab === "detailed" 
                    ? "text-gray-900" 
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Detailed
              </button>
            </div>
          </div>
          
          {/* Review Response Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Positive Review Card */}
            <Card className="bg-white border border-gray-100 shadow-md rounded-xl">
              <CardContent className="p-6">
                {/* Icon and Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <ThumbsUp className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Positive</h3>
                </div>
                
                {/* Customer Feedback */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer says:</p>
                  <p className="text-gray-600 italic">"Loved the quick service and friendly staff."</p>
                </div>
                
                {/* Our Response */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Our reply ({selectedTab === "short" ? "short" : "detailed"}):</p>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {selectedTab === "short" 
                        ? "Thank you, [Name]! We're glad the team made your visit easy. See you again soon."
                        : "Thank you so much, [Name]! We're thrilled to hear that our team provided quick and friendly service. Your positive feedback means the world to us and motivates our team. We can't wait to welcome you back and continue exceeding expectations. Have a wonderful day!"
                      }
                    </p>
                  </div>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <p className="text-sm font-medium text-gray-700">Encouraged repeat visit</p>
                </div>
              </CardContent>
            </Card>

            {/* Neutral Review Card */}
            <Card className="bg-white border border-gray-100 shadow-md rounded-xl">
              <CardContent className="p-6">
                {/* Icon and Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Neutral</h3>
                </div>
                
                {/* Customer Feedback */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer says:</p>
                  <p className="text-gray-600 italic">"Service was fine, wait time was a bit long."</p>
                </div>
                
                {/* Our Response */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Our reply ({selectedTab === "short" ? "short" : "detailed"}):</p>
                  <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {selectedTab === "short" 
                        ? "Thanks for the feedback, [Name]. We're working on reducing wait times. We'll prioritize your next visit!"
                        : "Thanks for the honest feedback, [Name]. We're adjusting peak-hour staffing this week to reduce wait times. If you share your preferred slot, we'll prioritize your next visit."
                      }
                    </p>
                  </div>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <p className="text-sm font-medium text-gray-700">Expectation reset</p>
                </div>
              </CardContent>
            </Card>

            {/* Negative Review Card */}
            <Card className="bg-white border border-gray-100 shadow-md rounded-xl">
              <CardContent className="p-6">
                {/* Icon and Title */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Negative</h3>
                </div>
                
                {/* Customer Feedback */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Customer says:</p>
                  <p className="text-gray-600 italic">"Didn't get the service I booked."</p>
                </div>
                
                {/* Our Response */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Our reply ({selectedTab === "short" ? "short" : "detailed"}):</p>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {selectedTab === "short" 
                        ? "We're sorry, [Name]. Let's make this right with a rebook or refund. Please DM your booking ID."
                        : "We're sorry about this experience, [Name]. We've flagged this internally and can make it right with a no-charge rebook or refund, whichever you prefer. Please DM your booking ID so we can resolve it today."
                      }
                    </p>
                  </div>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <p className="text-sm font-medium text-gray-700">Issue resolved or rebooked</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Footnote */}
          <div className="text-center mt-12">
            <p className="text-sm text-gray-600 max-w-2xl mx-auto">
              Sample responses for illustration. All responses are customized to your brand voice and specific situations.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 md:px-10 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your questions, answered
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about growing your business with Fly.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {FAQS.map(({ value, q, a }) => (
              <AccordionItem
                key={value}
                value={value}
                className="rounded-xl border border-gray-200 px-6 transition-colors data-[state=open]:border-purple-300"
              >
                <AccordionTrigger className="py-6 text-left text-lg font-semibold text-gray-900 hover:no-underline">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <p className="text-gray-600 leading-relaxed">{a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Ready to Attract More Clients Section */}
      <section className="py-16 px-4 md:px-10" style={{ background: WHATSAPP_TEAL }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to attract more clients from Google?
          </h2>
          <p className="text-white/90 text-lg mb-6">
            Try Fly WhatsApp Agent free for 14 days. No credit card required.
          </p>
          <Button
            size="lg"
            className="bg-white hover:bg-gray-100 px-8 py-4 text-lg font-bold rounded-2xl transition-all duration-300 hover:scale-105"
            style={{ color: WHATSAPP_TEAL }}
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/agent/lite';
              }
            }}
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Start free for 14 days
          </Button>
        </div>
      </section>

      {/* Get Your Free Growth Plan Section */}
      <section className="py-16 px-4 md:px-10 bg-white" data-section="growth-plan">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Get your free growth plan</h2>
          <p className="text-lg text-gray-600 mb-10">
            Tell us about your practice and we'll create a custom strategy to attract more patients from Google.
          </p>
          
          {planSend === 'sent' ? (
            <div role="status" className="max-w-xl mx-auto space-y-2 rounded-xl border border-violet-200 bg-violet-50 px-6 py-8">
              <CheckCircle className="mx-auto h-8 w-8 text-violet-700" aria-hidden />
              <p className="text-lg font-bold text-gray-900">Thanks, we have your details.</p>
              <p className="text-gray-600">Your growth plan reaches {plan.email} within 24 hours.</p>
            </div>
          ) : (
          <form className="space-y-6 max-w-xl mx-auto text-left" onSubmit={submitPlan}>
            <Input
              aria-label="Name"
              placeholder="Name*"
              name="name"
              autoComplete="name"
              required
              value={plan.name}
              onChange={onPlanField}
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Input
              aria-label="Business name"
              placeholder="Business name*"
              name="businessName"
              autoComplete="organization"
              required
              value={plan.businessName}
              onChange={onPlanField}
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Input
              aria-label="Phone number"
              placeholder="Phone number*"
              type="tel"
              name="phone"
              autoComplete="tel"
              required
              value={plan.phone}
              onChange={onPlanField}
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Input
              aria-label="Email"
              placeholder="Email*"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={plan.email}
              onChange={onPlanField}
              className="bg-white border-gray-300 text-gray-900 h-12 text-lg"
            />
            <Button
              type="submit"
              disabled={planSend === 'sending'}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold py-4 text-lg"
            >
              {planSend === 'sending' ? 'Sending...' : 'Get my free growth plan'}
            </Button>
            {planSend === 'failed' && (
              <p role="alert" className="text-sm font-medium text-red-700">{SITE_LEAD_ERROR}</p>
            )}
            <p className="text-center text-sm text-gray-600">
              Prefer to chat? <a href="https://wa.me/918446712833" target="_blank" rel="noopener noreferrer" className="font-semibold text-violet-700 underline underline-offset-2">Ask on WhatsApp</a>
            </p>
          </form>
          )}
          
          <p className="text-sm text-gray-600 mt-6">
            We'll analyze your current Google presence and send you a detailed growth plan within 24 hours.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Individuals;